import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, Select, DatePicker, Radio, Row, Col, Button, Upload, InputNumber, message } from 'antd';
import moment from 'moment';
import { useSelector } from "react-redux";
import { Map, TileLayer, Marker, Popup, Polygon, Tooltip, FeatureGroup } from 'react-leaflet';
import { apiEndpoint } from '@iso/lib/helpers/endpoint';
import { incidentService, attachment as attachmentService, propertyService } from '@iso/services';
import IntlMessages from '@iso/components/utility/intlMessages';
import IncidentModalWrapper from './IncidentModal.styles';
import { authHeader } from "@iso/lib/helpers/authHeader";
import { UploadOutlined } from '@ant-design/icons';
import {
  TYPES,
  OUTCOMES,
  normalizeIncidentTypeValue,
  normalizeIncidentOutcomeValue,
  findIncidentTypeOption,
  findIncidentOutcomeOption,
} from '@iso/constants/incidentOptions';

const TYPE_REQUIRED_FIELDS = {
  notifiable_incident: ['personnel_notified', 'immediate_actions'],
  near_miss: ['immediate_actions'],
  injury_illness: ['injury_details'],
  environmental: ['environment_impact_details'],
  property_damage: ['damage_details'],
  security_incident: ['scene_secured', 'personnel_notified'],
  reputational: ['personnel_notified'],
  psychosocial: ['injury_details'],
  process_safety: ['immediate_actions'],
  contractor_incident: ['personnel_notified'],
  public_safety_incident: ['personnel_notified', 'immediate_actions'],
  regulatory_breach: ['personnel_notified'],
};

const OUTCOME_REQUIRED_FIELDS = {
  regulatory_notification: ['personnel_notified'],
  corrective_action: ['immediate_actions'],
  medical_treatment: ['injury_details'],
  lost_time: ['injury_details'],
  environmental_remediation: ['environment_impact_details'],
  asset_repair_or_replacement: ['damage_details'],
  security_response: ['scene_secured'],
  reputation_management: ['personnel_notified'],
  employee_support: ['injury_details'],
  operational_disruption: ['immediate_actions'],
  contractor_review: ['personnel_notified'],
  community_engagement: ['personnel_notified'],
  compliance_review: ['immediate_actions'],
  insurance_claim: ['damage_details'],
  investigation: ['immediate_actions'],
  training_or_communication: [],
};

const { Option } = Select;
const { TextArea } = Input;

// Default coordinates (Uluru)
const DEFAULT_LAT = -25.3444;
const DEFAULT_LNG = 131.0369;

const readActivePropertyFromStorage = () => {
  try {
    const stored = localStorage.getItem('active_property');
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn('Unable to parse active_property from localStorage', error);
    return null;
  }
};

const IncidentModal = ({ visible, onCancel, incident, onSaved, propertyId: propertyIdProp }) => {
  const reduxPropertyId = useSelector(state => state.property.activePropertyId);
  const propertyId = propertyIdProp || reduxPropertyId;
  const mapRef = useRef();

  const [form] = Form.useForm();
  const [riskAssessmentConducted, setRiskAssessmentConducted] = useState(null);
  const [loading, setLoading] = useState(false);
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [defaultLatitude, setDefaultLatitude] = useState(DEFAULT_LAT);
  const [defaultLongitude, setDefaultLongitude] = useState(DEFAULT_LNG);
  const [attachmentIds, setAttachmentIds] = useState([]);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [activeProperty, setActiveProperty] = useState(() => readActivePropertyFromStorage());
  const [selectedTypeDescription, setSelectedTypeDescription] = useState(null);
  const [selectedOutcomeDescription, setSelectedOutcomeDescription] = useState(null);
  const [requiredFields, setRequiredFields] = useState(new Set());
  const [personnelOptions, setPersonnelOptions] = useState([]);
  const [mapInstance, setMapInstance] = useState(null);
  const [primaryObjects, setPrimaryObjects] = useState([]);

  // Debug: Log when modal state changes
  console.log('[IncidentModal] Render - visible:', visible, 'propertyId:', propertyId);

  const updateFieldRequirements = (typeValue, outcomeValue) => {
    const normalizedTypeValue = normalizeIncidentTypeValue(typeValue);
    const normalizedOutcomeValue = normalizeIncidentOutcomeValue(outcomeValue);

    const fields = new Set();

    if (normalizedTypeValue && TYPE_REQUIRED_FIELDS[normalizedTypeValue]) {
      TYPE_REQUIRED_FIELDS[normalizedTypeValue].forEach(field => fields.add(field));
    }

    if (normalizedOutcomeValue && OUTCOME_REQUIRED_FIELDS[normalizedOutcomeValue]) {
      OUTCOME_REQUIRED_FIELDS[normalizedOutcomeValue].forEach(field => fields.add(field));
    }

    setRequiredFields(fields);
  };

  const buildRequiredRule = (field, message) => (
    requiredFields.has(field) ? [{ required: true, message }] : []
  );

  useEffect(() => {
    let isMounted = true;

    if (!propertyId) {
      setPersonnelOptions([]);
      return () => {
        isMounted = false;
      };
    }

    propertyService.getListUsersOfProperty(propertyId, true)
      .then((res) => {
        if (!isMounted) {
          return;
        }

        const users = Array.isArray(res?.users)
          ? res.users
          : Array.isArray(res?.data?.users)
            ? res.data.users
            : [];

        const uniqueNames = Array.from(new Set(users.map((user) => {
          const displayName = user?.name || user?.email;
          if (!displayName) {
            return null;
          }
          if (user?.name && user?.email && user.name !== user.email) {
            return `${user.name} (${user.email})`;
          }
          return displayName;
        }).filter(Boolean)));
        setPersonnelOptions(uniqueNames);
      })
      .catch((error) => {
        console.error('Failed to load personnel options', error);
      });

    return () => {
      isMounted = false;
    };
  }, [propertyId]);

  useEffect(() => {
    let isMounted = true;

    const normalizeCoordinates = (property) => {
      if (!property) {
        return property;
      }

      let { lat, lng } = property;

      if (lat == null || lng == null) {
        if (typeof property.location === 'string') {
          const match = property.location.match(/POINT\s*\(\s*([-+]?\d*\.?\d+)\s+([-+]?\d*\.?\d+)\s*\)/i);
          if (match) {
            lat = parseFloat(match[1]);
            lng = parseFloat(match[2]);
          }
        } else if (property.location && Array.isArray(property.location.coordinates)) {
          const [lngCoord, latCoord] = property.location.coordinates;
          lat = latCoord ?? lat;
          lng = lngCoord ?? lng;
        }
      }

      return {
        ...property,
        lat,
        lng,
      };
    };

    const ensurePropertyCoordinates = async () => {
      const stored = readActivePropertyFromStorage();
      if (isMounted) {
        setActiveProperty(stored);
      }

      const storedWithCoords = normalizeCoordinates(stored);

      if (storedWithCoords?.lat != null && storedWithCoords?.lng != null) {
        setDefaultLatitude(storedWithCoords.lat);
        setDefaultLongitude(storedWithCoords.lng);
        if (stored !== storedWithCoords) {
          localStorage.setItem('active_property', JSON.stringify(storedWithCoords));
          setActiveProperty(storedWithCoords);
        }
        return;
      }

      if (!propertyId) {
        return;
      }

      try {
        const response = await propertyService.viewProperty(propertyId);
        if (!isMounted) {
          return;
        }

        const propertyData = response?.property || response?.data || null;
        if (propertyData) {
          const normalized = normalizeCoordinates(propertyData);
          if (normalized.lat != null && normalized.lng != null) {
            setDefaultLatitude(normalized.lat);
            setDefaultLongitude(normalized.lng);
          }
          localStorage.setItem('active_property', JSON.stringify(normalized));
          setActiveProperty(normalized);
        }
      } catch (error) {
        console.error('Failed to load property coordinates', error);
      }
    };

    ensurePropertyCoordinates();

    return () => {
      isMounted = false;
    };
  }, [propertyId]);

  useEffect(() => {
    if (activeProperty && (latitude == null || longitude == null)) {
      setLatitude(activeProperty.lat);
      setLongitude(activeProperty.lng);
    }
  }, [activeProperty]);

  // Force map to recalculate size when modal becomes visible
  useEffect(() => {
    if (visible && mapInstance) {
      setTimeout(() => {
        mapInstance.invalidateSize();
      }, 100);
    }
  }, [visible, mapInstance]);

  // Fetch primary objects (polygons) when modal opens
  useEffect(() => {
    if (visible && propertyId) {
      console.log('[IncidentModal] Fetching primary objects for property:', propertyId);
      propertyService.viewProperty(propertyId).then(res => {
        console.log('[IncidentModal] Property response:', res);
        if (res.code === '0000' && res.primary_objects) {
          console.log('[IncidentModal] Setting primary objects:', res.primary_objects);
          setPrimaryObjects(res.primary_objects);
        } else {
          console.warn('[IncidentModal] No primary objects in response');
        }
      }).catch(err => {
        console.error('[IncidentModal] Error loading primary objects:', err);
      });
    }
  }, [visible, propertyId]);

  useEffect(() => {
    if (incident) {
      const personnelNotifiedList = incident.personnel_notified
        ? incident.personnel_notified.split(',').map((item) => item.trim()).filter(Boolean)
        : [];
      const normalizedType = normalizeIncidentTypeValue(incident.type);
      const normalizedOutcome = normalizeIncidentOutcomeValue(incident.consequence);

      form.setFieldsValue({
        ...incident,
        date_time: incident.date_time ? moment(incident.date_time) : null,
        latitude: incident.latitude,
        longitude: incident.longitude,
        type: normalizedType,
        consequence: normalizedOutcome,
        personnel_notified: personnelNotifiedList,
      });
      setRiskAssessmentConducted(incident.risk_assessment_conducted);
      const typeOption = findIncidentTypeOption(incident.type);
      setSelectedTypeDescription(typeOption ? typeOption.description : null);
      const outcomeOption = findIncidentOutcomeOption(incident.consequence);
      setSelectedOutcomeDescription(outcomeOption ? outcomeOption.description : null);
      updateFieldRequirements(incident.type, incident.consequence);
      setUploadFiles(
        incident.attachments.map((attachment, index) => ({
          id: attachment.id,
          uid: attachment.id,
          name: attachment.url.split('/').pop(),
          url: attachment.full_path,
          thumbUrl: attachment.full_path,
        }))
      );
      setLatitude(incident.latitude);
      setLongitude(incident.longitude);
      setDefaultLatitude(incident.latitude);
      setDefaultLongitude(incident.longitude);
      setAttachmentIds(incident.attachments.map(attachment => attachment.id));
      if (personnelNotifiedList.length) {
        setPersonnelOptions((prev) => {
          const set = new Set([...prev, ...personnelNotifiedList]);
          return Array.from(set);
        });
      }
    } else {
      form.resetFields();
      setRiskAssessmentConducted(null);
      setAttachmentIds([]);
      setUploadFiles([]);
      setSelectedTypeDescription(null);
      setSelectedOutcomeDescription(null);
      setRequiredFields(new Set());
    }
  }, [incident, form]);

  const handleFormChange = (changedValues) => {
    if (Object.prototype.hasOwnProperty.call(changedValues, 'type')) {
      const option = findIncidentTypeOption(changedValues.type);
      setSelectedTypeDescription(option ? option.description : null);
    }

    if (Object.prototype.hasOwnProperty.call(changedValues, 'consequence')) {
      const option = findIncidentOutcomeOption(changedValues.consequence);
      setSelectedOutcomeDescription(option ? option.description : null);
    }

    const currentType = Object.prototype.hasOwnProperty.call(changedValues, 'type')
      ? changedValues.type
      : form.getFieldValue('type');

    const currentOutcome = Object.prototype.hasOwnProperty.call(changedValues, 'consequence')
      ? changedValues.consequence
      : form.getFieldValue('consequence');

    updateFieldRequirements(currentType, currentOutcome);
  };

  const handleRiskAssessmentChange = value => {
    setRiskAssessmentConducted(value);
  };

  const onStore = async (values) => {
    values.property_id = propertyId;
    values.attachment_ids = attachmentIds;
    values.type = normalizeIncidentTypeValue(values.type);
    values.consequence = normalizeIncidentOutcomeValue(values.consequence);
    if (Array.isArray(values.personnel_notified)) {
      values.personnel_notified = values.personnel_notified.join(', ');
    }

    setLoading(true);

    try {
      const res = await incidentService.createOrUpdate(values, incident?.id);
      if (typeof onSaved === 'function') {
        onSaved(res);
      }
      message.success(incident ? 'Incident updated successfully.' : 'Incident created successfully.');
      return true;
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Failed to save incident.';
      message.error(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const success = await onStore(values);
      if (success) {
        form.resetFields();
        handleCancel();
      }
    } catch (error) {
      console.log('Incident form validation failed', error);
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleClickMap = (e) => {
    const { lat, lng } = e.latlng;

    form.setFieldsValue({
       latitude: lat,
       longitude: lng,
    });

    setLatitude(lat);
    setLongitude(lng);
  };

  // Render primary objects (polygons) on the map
  const renderPrimaryObjects = () => {
    console.log('[IncidentModal] renderPrimaryObjects called, primaryObjects:', primaryObjects);
    if (!primaryObjects || primaryObjects.length === 0) {
      console.log('[IncidentModal] No primary objects to render');
      return null;
    }

    console.log('[IncidentModal] Rendering', primaryObjects.length, 'polygons');
    return primaryObjects.map((object) => {
      const colorMap = {
        'blue': '#31a6c5',
        'green': '#52c41a',
        'red': '#f5222d',
        'yellow': '#faad14',
        'purple': '#722ed1',
        'orange': '#fa8c16',
      };

      const color = colorMap[object.color] || '#31a6c5';

      return (
        <Polygon
          key={object.id}
          positions={object.area}
          fillColor={color}
          color={color}
          fillOpacity={0.2}
          weight={2}
        >
          <Tooltip permanent={true} direction="center" className="primary-objects-tooltip">
            <div>
              <p style={{ margin: 0, fontWeight: 'bold' }}>{object.name}</p>
            </div>
          </Tooltip>
        </Polygon>
      );
    });
  };

  const handleMapReady = (map) => {
    setMapInstance(map.target);
    // Force recalculation of map size
    setTimeout(() => {
      map.target.invalidateSize();
    }, 100);
  };

  const onFileChange = (response) => {
    if(response.file && response.file.status === "done"){
      handleUploadAttachmentSuccess(response.file.response.attachment);
    }

    setUploadFiles([...response.fileList]);
  };

  const handleUploadAttachmentSuccess = (attachment) => {
    setAttachmentIds(attachmentIds => [...attachmentIds, attachment.id]);
  }

  const handleRemoveAttachment = (attachment) => {
    const removedAttachmentId = attachment.response?.attachment?.id ?? attachment.id;

    if (removedAttachmentId) {
      attachmentService.destroy(removedAttachmentId);
    }
  }

  // Calculate map center with fallback
  const mapCenter = (() => {
    // If user has selected a location, use that
    if (latitude != null && longitude != null) {
      return [latitude, longitude];
    }

    // Use polygon centroid if available
    if (primaryObjects && primaryObjects.length > 0) {
      const firstObject = primaryObjects[0];
      if (firstObject.lat_centroid && firstObject.lng_centroid) {
        return [firstObject.lat_centroid, firstObject.lng_centroid];
      }
    }

    // Fall back to property center or default
    return [defaultLatitude ?? DEFAULT_LAT, defaultLongitude ?? DEFAULT_LNG];
  })();

  // Generate map key to force re-render when coordinates change
  const mapKey = `${defaultLatitude}-${defaultLongitude}`;

  return (
    <IncidentModalWrapper
      visible={visible}
      onCancel={handleCancel}
      maskClosable={false}
      title={incident ? "Update Incident" : "Record an Incident"} 
      footer={[
        <Button key="back" onClick={handleCancel} className="cancel-button">
          {<IntlMessages id="propertyPage.modal.cancel" />}
        </Button>,
        <Button key="submit" className="btn-success" type="primary" onClick={handleSubmit} loading={loading}>
          {<IntlMessages id="propertyPage.modal.save" />}
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        name="incident_form"
        initialValues={{ date_time: moment() }}
        scrollToFirstError={true}
        onValuesChange={handleFormChange}
      >
        <div className="form-body">
          <Form.Item
            className="isoInputWrapper"
            name="date_time"
            label="Date and Time of Incident"
            rules={[{ required: true, message: 'Please select date and time!' }]}
          >
            <DatePicker showTime size="large" placeholder="Select date and time" />
          </Form.Item>
        <Form.Item
            className="isoInputWrapper"
            name="type"
            label="Type of Incident"
            rules={[{ required: true, message: 'Please select type of incident!' }]}
            extra={selectedTypeDescription ? (
              <span className="incident-type-help">{selectedTypeDescription}</span>
            ) : null}
          >
            <Select size="large" placeholder="Select type of incident">
              {TYPES.map(type => (
                <Option key={type.value} value={type.value}>
                  <div className="incident-type-option">
                    <div className="incident-type-label">{type.label}</div>
                    <div className="incident-type-description">{type.description}</div>
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            className="isoInputWrapper"
            name="activity"
            label="Type of Activity"
            rules={[{ required: true, message: 'Please enter type of activity!' }]}
          >
            <Input size="large" placeholder="Enter type of activity" />
          </Form.Item>
          <Form.Item
            className="isoInputWrapper"
            name="description"
            label="Description of Incident/Event"
            rules={[{ required: true, message: 'Please enter description!' }]}
          >
            <TextArea rows={4} size="large" placeholder="Enter description of incident/event" />
          </Form.Item>
          <Form.Item
            className="isoInputWrapper"
            name="contractor_visitor_involved"
            label="Was a contractor/visitor involved or witnessed the incident?"
            rules={[{ required: true, message: 'Please select an option!' }]}
          >
            <Radio.Group size="large">
              <Radio value={1}>Yes</Radio>
              <Radio value={0}>No</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            className="isoInputWrapper"
            label="Location of Incident"
            rules={[{ required: true, message: 'Please select location!' }]}
            style={{marginBottom:'0px'}}
          >
            <Row>
              <Col xs={{span: 24}} md={{span: 24}} style={{marginBottom:'10px'}}>
                <Map
                  key={mapKey}
                  ref={mapRef}
                  center={mapCenter}
                  doubleClickZoom={false}
                  onClick={handleClickMap}
                  whenReady={handleMapReady}
                  zoom={16}
                  zoomControl={true}
                  style={{width:'100%', height:'300px'}}
                >
                <TileLayer
                  attribution='&copy; Google Maps'
                  url="https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}"
                />
                <FeatureGroup>
                  {/* Render property polygon overlays */}
                  {renderPrimaryObjects()}
                </FeatureGroup>
                  {(latitude != null && longitude != null) && (
                    <Marker position={[latitude, longitude]} draggable={false}>
                      <Popup position={[latitude, longitude]}>
                      </Popup>
                    </Marker>
                  )}
                </Map>
              </Col>
            
              <Col xs={{span: 24}} md={{span: 11}}> 
                <Form.Item
                  name='latitude'
                  label="Latitude"
                  rules={[
                    {
                      required: true,
                      message: 'This field is required',
                    },
                  ]}
                >                          
                  <InputNumber
                    value={latitude}
                    onChange={setLatitude}
                    bordered={false}
                    size="large"
                    placeholder="Enter latitude"
                  />
                </Form.Item>
              </Col>
              <Col xs={{span: 24}} md={{span: 11, offset: 2}} >
                <Form.Item
                  className="isoInputWrapper"
                  name='longitude'
                  label="Longitude"
                  rules={[
                    {
                      required: true,
                      message: 'This field is required',
                    },
                  ]}
                >
                  <InputNumber
                    value={longitude}
                    onChange={setLongitude}
                    bordered={false}
                    size="large"
                    placeholder="Enter longitude"
                  />
                </Form.Item>
              </Col> 
            </Row>
          </Form.Item>

          <Form.Item
            className="isoInputWrapper"
            name="injury_details"
            label="Details of any injury"
            rules={buildRequiredRule('injury_details', 'Please enter details of any injury!')}
          >
            <TextArea rows={2} size="large" placeholder="Enter details of any injury" />
          </Form.Item>
          <Form.Item
            className="isoInputWrapper"
            name="damage_details"
            label="Details of any damage"
            rules={buildRequiredRule('damage_details', 'Please enter details of any damage!')}
          >
            <TextArea rows={2} size="large" placeholder="Enter details of any damage" />
          </Form.Item>
          <Form.Item
            className="isoInputWrapper"
            name="environment_impact_details"
            label="Details of any impact to environment"
            rules={buildRequiredRule('environment_impact_details', 'Please enter details of any impact to the environment!')}
          >
            <TextArea rows={2} size="large" placeholder="Enter details of any impact to environment" />
          </Form.Item>
          <Form.Item
            className="isoInputWrapper"
            name="immediate_actions"
            label="Immediate actions taken following the event"
            rules={buildRequiredRule('immediate_actions', 'Please enter immediate actions!')}
          >
            <TextArea rows={2} size="large" placeholder="Enter immediate actions taken following the event" />
          </Form.Item>
          <Form.Item
            className="isoInputWrapper"
            name="personnel_notified"
            label="Personnel reported to"
            rules={buildRequiredRule('personnel_notified', 'Please select at least one person notified!')}
          >
            <Select
              mode="tags"
              size="large"
              allowClear
              tokenSeparators={[',']}
              placeholder="Select or type the person notified"
              options={personnelOptions.map((name) => ({ value: name, label: name }))}
            />
          </Form.Item>
          <Form.Item
            className="isoInputWrapper"
            name="scene_secured"
            label="How was the scene secured?"
            rules={buildRequiredRule('scene_secured', 'Please enter how the scene was secured!')}
          >
            <TextArea rows={2} size="large" placeholder="Enter how the scene was secured" />
          </Form.Item>
          <Form.Item
            className="isoInputWrapper"
            name="risk_assessment_conducted"
          label="Was an SOP or Permit to Work/JSA in place for this task?"
            rules={[{ required: true, message: 'Please select an option!' }]}
          >
            <Select onChange={handleRiskAssessmentChange} size="large" placeholder="Select an option">
              <Option value={1}>Yes</Option>
              <Option value={0}>No</Option>
            </Select>
          </Form.Item>

          {riskAssessmentConducted === 0 && (
            <Form.Item
              className="isoInputWrapper"
              name="risk_assessment_not_completed_reason"
              label="Please comment why an SOP or Permit to Work/JSA was not in place?"
              rules={[{ required: true, message: 'Please provide a reason!' }]}
            >
              <TextArea rows={2} size="large" placeholder="Enter reason why an SOP or JSA was not in place" />
            </Form.Item>
          )}

          {riskAssessmentConducted === 1 && (
            <>
              <Form.Item
                className="isoInputWrapper"
                name="risk_assessment_details"
                label="What type of SOP or Permit to Work/JSA was in place?"
                rules={[{ required: true, message: 'Please enter SOP or JSA details!' }]}
              >
                <TextArea rows={2} size="large" placeholder="Enter SOP or JSA details" />
              </Form.Item>
              <Form.Item
                className="isoInputWrapper"
                name="sop_policy_details"
                label="Were additional controls or policies applied? If yes, please detail." 
                rules={[{ required: true, message: 'Please enter control or policy details!' }]}
              >
                <TextArea rows={2} size="large" placeholder="Enter control or policy details" />
              </Form.Item>
            </>
          )}
          <Form.Item
            className="isoInputWrapper"
            name="consequence"
            label="Outcome"
            rules={[{ required: true, message: 'Please select an outcome!' }]}
            extra={selectedOutcomeDescription ? (
              <span className="incident-type-help">{selectedOutcomeDescription}</span>
            ) : null}
          >
            <Select size="large" placeholder="Select outcome">
              {OUTCOMES.map(outcome => (
                <Option key={outcome.value} value={outcome.value}>
                  <div className="incident-type-option">
                    <div className="incident-type-label">{outcome.label}</div>
                    <div className="incident-type-description">{outcome.description}</div>
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            className="isoInputWrapper"
            label="Attach photos, videos, documents"
          >
            <Upload
              action={apiEndpoint.attachments}
              headers={authHeader(true)}
              listType="picture"
              name="Attachment[file]"
              fileList={uploadFiles}
              multiple={true}
              onChange={onFileChange}
              data={{ type: 'incident', storage: 'filesystem' }}
              onRemove={handleRemoveAttachment}
            >
              <Button icon={<UploadOutlined />}>Upload</Button>
            </Upload>
          </Form.Item>
        </div>
      </Form>
    </IncidentModalWrapper>
  );
};

export default IncidentModal;